/**
 * @orgus/protocol: the contract shared by agent, hub and UI.
 *
 * Types, protocol messages, action kinds, constants and small pure helpers.
 * No runtime dependencies and no Node built-ins: the UI imports this file too.
 * See docs/architecture/spec.md for the meaning of every field.
 */
import type { SessionTokenUsage } from './ext/costs.ts';

import type { AgentKind, AgentRuntime } from './ext/adapters.ts';
import type { CanonicalSessionState } from './ext/missions.ts';
import type { MachineReadiness } from './ext/readiness.ts';

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export type Id = string;
/** Epoch milliseconds. */
export type Ts = number;

export const PROTOCOL_VERSION = 1;

import type { Activity } from './ext/timeline.ts';
import type { AuthEndedEvent } from './ext/auth.ts';
import type { PipelineBatch } from './ext/pipelines.ts';
import type { WorktreeIntegrateParams, WorktreeLandedParams } from './ext/worktree-integrate.ts';
import { trimEndOf } from './ext/trim.ts';

// ---------------------------------------------------------------------------
// Data model
// ---------------------------------------------------------------------------

export type MachineOs = 'windows' | 'macos' | 'linux';

/** Bytes and messages a node has sent its hub since it connected, with the bytes by message kind. */
export interface MachineWire {
  /** When this connection opened (the hello counts). */
  since: Ts;
  msgs: number;
  bytes: number;
  kinds: Record<string, number>;
  /**
   * The last whole minute, so the steady rate reads apart from the connect burst (a snapshot
   * of every past session can be a megabyte). Absent in the first minute.
   */
  lastMinute?: { msgs: number; bytes: number };
}

export interface Machine {
  id: Id;
  name: string;
  os: MachineOs;
  arch: string;
  hostname: string;
  cpus: number;
  memTotal: number;
  memFree: number;
  /** Busy fraction, 0..1. */
  load: number;
  agentVersion: string;
  connectedAt: Ts | null;
  lastSeen: Ts;
  online: boolean;
  labels: string[];
  /** Agent CLIs on this machine (adapters' detect()); absent from agents that predate adapters. */
  runtimes?: AgentRuntime[];
  /**
   * The agent kinds this machine's agent can start (its adapters), reported from its first
   * message. Absent: an agent that predates adapters, which starts Claude Code whatever
   * agentKind asks for.
   */
  agentKinds?: AgentKind[];
  /**
   * The agent kinds whose ended sessions this machine's agent can resume headless (a wake,
   * session.start with resumeSessionId), from each adapter's own flag. Absent: an agent that
   * predates the list, which resumes Claude Code only (machineResumesKind). Additive.
   */
  resumeKinds?: AgentKind[];
  /**
   * The agent kinds this machine's agent runs AI jobs on (module 'aijobs': it has a runner for
   * them; whether the CLI is installed is in runtimes). The hub asks only for these. Absent: an
   * agent that predates the list, asked as before. Additive, 21 Oct 2026.
   */
  aiJobKinds?: AgentKind[];
  /** Set by the hub on the machine its own in-process agent runs on (`orgus up`). */
  local?: boolean;
  /**
   * ADR 0104: what this machine's agent has sent the hub since it connected, reported in the
   * once-a-minute machine refresh, so the Capacity page shows each node's wire cost and the
   * fleet's total from the fleet's own numbers. Absent from agents that predate it.
   */
  wire?: MachineWire;
  /**
   * What the agent found on this machine (R-ON-4, ADR 0090 decision 4): harnesses and their
   * sign-in state, git, repositories. Sent in hello's machine and refreshed by module
   * 'readiness'; a server keeps it only through acceptReadiness(), and the last report stands
   * when a message has none. Absent: an agent that predates it, or no report yet.
   */
  readiness?: MachineReadiness;
}

/** One sample of a machine's load and used memory (fractions 0..1), for sparklines. */
export interface MachineMetricSample {
  at: Ts;
  load: number;
  mem: number;
}

/** What an agent reports about itself in `hello`: no id until the hub has enrolled it. */
export type MachineInfo = Omit<Machine, 'id' | 'connectedAt' | 'lastSeen' | 'online'> & { id?: Id };

/** A repository, identified across machines. */
export interface Project {
  /** Stable: hash of the normalized origin URL, else of the name (computed by the agent). */
  id: Id;
  name: string;
  remoteUrl: string | null;
  defaultBranch: string;
  /** Adapter ids detected on any checkout. */
  adapters: string[];
}

export type CheckoutKind = 'main' | 'worktree';

/** One working tree of a project on one machine. */
export interface Checkout {
  id: Id;
  projectId: Id;
  machineId: Id;
  path: string;
  kind: CheckoutKind;
  branch: string | null;
  head: string;
  headSubject: string;
  /** Versus origin/<defaultBranch>. */
  ahead: number;
  behind: number;
  /**
   * Of the commits ahead, those whose work is not on the base yet: a project that lands by
   * cherry-pick or rebase (Qawl) puts copies on main, so a landed branch stays ahead by hash.
   * Non-merge commits whose subject no base commit carries. Absent: not measured, read ahead.
   */
  unlanded?: number;
  /** Count of changed paths. */
  dirty: number;
  updatedAt: Ts;
  error?: string;
}

export type SessionStatus = 'working' | 'waiting' | 'idle' | 'ended';
export const SESSION_STATUSES: readonly SessionStatus[] = ['working', 'waiting', 'idle', 'ended'];

export interface TokenUsage {
  input: number;
  output: number;
  cacheRead: number;
}

/** One Claude Code session (one transcript). */
export interface Session {
  /** The transcript's session id. */
  id: Id;
  machineId: Id;
  projectId: Id | null;
  checkoutId: Id | null;
  cwd: string;
  /** First user prompt, trimmed to 140 chars. */
  title: string;
  /**
   * working: assistant/tool activity under 60 s ago and process alive.
   * waiting: last entry asks the user (a question tool, or a turn whose last message
   *   ends in a question) or awaits permission.
   * idle: process alive, no activity.
   * ended: no live process.
   */
  status: SessionStatus;
  pid: number | null;
  model: string | null;
  permissionMode: string | null;
  startedAt: Ts;
  lastActivity: Ts;
  turns: number;
  toolCalls: number;
  tokens: TokenUsage;
  /**
   * Last assistant text, 280 chars on one line: its head, or its end (`…` first) when the
   * turn ends on a question, so a row reads the question (ADR 0056). The whole message of a
   * session waiting on a person is `ext.asks` (SessionAsk, protocol ext/asks.ts).
   */
  lastText: string;
  /** The newest subagents (bounded); `subagentsTotal` is how many there are. */
  subagents: Subagent[];
  /** Running runs first, then the newest (bounded); `workflowsTotal` is how many there are. */
  workflows: WorkflowRun[];
  /** Tokens per model including subagents, for module costs. Optional: older agents omit it. */
  usage?: SessionTokenUsage;
  /** orgus = started from the portal. */
  origin: 'interactive' | 'orgus';
  /**
   * turns, toolCalls and tokens are lower bounds: the transcript is long and the agent
   * has read only its tail so far (a one-time full read in the background clears this).
   */
  partial?: boolean;
  /** All subagents of the session (older than the lookback too), when more than `subagents` holds. */
  subagentsTotal?: number;
  /** All workflow runs in the lookback window, when more than `workflows` holds. */
  workflowsTotal?: number;
  /** The adapter that reported it; absent means Claude Code (agentKindOf). */
  agentKind?: AgentKind;
  /** The adapter's mapping onto the canonical state machine (sessionStateOf falls back to status). */
  state?: CanonicalSessionState;
  /** Agent-specific data, owned by the adapter of agentKind (CodexSessionExt for codex). */
  ext?: Record<string, unknown>;
}

export type RunStatus = 'running' | 'done' | 'failed' | 'stopped';
export const RUN_STATUSES: readonly RunStatus[] = ['running', 'done', 'failed', 'stopped'];

export interface Subagent {
  id: Id;
  sessionId: Id;
  type: string;
  description: string;
  status: RunStatus;
  startedAt: Ts;
  endedAt: Ts | null;
  toolCalls: number;
  lastText: string;
  /** toolCalls is a lower bound (a long subagent transcript read from its tail). */
  partial?: boolean;
  /** When it actually worked: bursts, resumes, compactions (module timeline). */
  activity?: Activity;
}

export interface WorkflowPhase {
  title: string;
  agents: number;
  done: number;
}

/** One agent of a workflow run (a lane of its own on the Agents page). */
export interface WorkflowAgent {
  id: Id;
  /** The label the workflow script gave it, for example 'build:costs'. */
  label: string;
  /** The phase title it ran in ('' when the script named none). */
  phase: string;
  status: RunStatus;
  startedAt: Ts;
  endedAt: Ts | null;
  toolCalls: number;
  /** Last assistant text (or the result preview of a finished run), 280 chars. */
  lastText: string;
  /** The git worktree it worked in, when it was spawned with one. */
  worktree: string | null;
  /**
   * Which run of its label this is (1, 2, ...), set only when the same label ran more than
   * once one after another (a resumed or retried run starts it again under a new id).
   */
  attempt?: number;
  /** The id of the later agent that took over its label: this one is an earlier attempt. */
  supersededBy?: Id;
  /** When it actually worked: bursts, resumes, compactions (module timeline). */
  activity?: Activity;
}

/** Agents listed per workflow run at most; beyond it `agentsOmitted` says how many were left out. */
export const WORKFLOW_AGENTS_MAX = 1000;

export interface WorkflowRun {
  id: Id;
  sessionId: Id;
  name: string;
  description: string;
  status: RunStatus;
  phases: WorkflowPhase[];
  startedAt: Ts;
  endedAt: Ts | null;
  /** Every agent of the run, in start order (older agents may not report it: treat missing as []). */
  agents: WorkflowAgent[];
  /** Agents left out of `agents` because the run has more than WORKFLOW_AGENTS_MAX. */
  agentsOmitted?: number;
}

export type PipelineState = 'running' | 'idle' | 'paused' | 'down' | 'error';

/** Pipelines come from adapters. One shape for every project, so the UI renders any of them. */
export interface Pipeline {
  projectId: Id;
  machineId: Id;
  /** For example 'qawl-land' or 'generic-git'. */
  adapter: string;
  /** For example 'Landing queue'. */
  title: string;
  state: PipelineState;
  /** Ordered, rendered left to right. */
  stages: PipelineStage[];
  /** What is in flight or queued. */
  items: PipelineItem[];
  /** The last 20 finished. */
  recent: PipelineItem[];
  /** For example a worker pool. */
  capacity?: PipelineCapacity;
  /** The batch the runner works on now, for a runner that takes one batch at a time (older agents do not report it). */
  batch?: PipelineBatch;
  /** Which pipeline actions this adapter supports. */
  actions: ActionKind[];
  updatedAt: Ts;
  error?: string;
  /** Why nothing moves although work waits (main is red, so landings wait). */
  hold?: string;
}

export interface PipelineCapacity {
  used: number;
  total: number;
  /** Claims waiting for a slot. */
  waiting: number;
  /** What a slot is, plural, for example 'Test workers'. */
  label: string;
  /** Who holds the busy slots right now (older agents do not report it). */
  holders?: PipelineCapacityClaim[];
  /** The claims waiting for a slot, oldest first (older agents do not report it). */
  queue?: PipelineCapacityClaim[];
  /** Distinct live runs (processes) holding slots, for example verify runs. */
  runs?: number;
  /** What one run is, singular, for example 'verify run'. */
  runLabel?: string;
  /**
   * Slots kept back for one run (Qawl's landing gate) and how many of them it is using now.
   * Its own claims draw on them first, so only the unused part counts as busy on its own
   * (that part is the reserved claim in `holders`).
   */
  reserve?: { slots: number; inUse: number; for: string };
}

/** One claim on a capacity pool: `slots` slots held (or wanted) for one task of one run. */
export interface PipelineCapacityClaim {
  /** The task, for example 'console:test'. */
  label: string;
  slots: number;
  pid?: number;
  /** Which run it belongs to, in words, for example 'landing queue'. */
  run?: string;
  /** When the claim was made. */
  since?: Ts;
  /** Slots kept back for work to come (not a run): counted as busy, not as a run. */
  reserved?: boolean;
}

export interface PipelineStage {
  id: string;
  title: string;
}

export type PipelineItemState = 'queued' | 'active' | 'passed' | 'failed' | 'cancelled';

export interface PipelineItem {
  id: Id;
  title: string;
  detail?: string;
  /** A stage id. */
  stage: string;
  state: PipelineItemState;
  priority?: boolean;
  by?: string;
  /** When it was first submitted. */
  startedAt?: Ts;
  /** When it entered its current stage or run (a retry restarts it); missing means startedAt. */
  stageStartedAt?: Ts;
  endedAt?: Ts;
  logRef?: string;
  /** pipeline.json's short labels, for example a failed landing's fate (LANDING_FATE). */
  tags?: string[];
  /** What the change touches, on a landing that ran checks: what forecasts compare (ADR 0091). */
  scope?: PipelineItemScope;
}

/** The size and reach of a change in a pipeline item (Project Protocol `scope`, ADR 0091). */
export interface PipelineItemScope {
  /** Files changed. */
  files: number;
  /** The units it touches (apps/hub, packages/ui, docs ...), sorted, at most 30. */
  units: string[];
  /** Every changed file is Markdown. */
  docsOnly?: boolean;
}

export type JobState = 'running' | 'passed' | 'failed' | 'cancelled';

/** A command run by an agent on request. */
export interface Job {
  id: Id;
  machineId: Id;
  checkoutId: Id | null;
  cwd: string;
  command: string;
  state: JobState;
  startedAt: Ts;
  endedAt: Ts | null;
  exitCode: number | null;
}

export type FeedEventKind =
  | 'session.started'
  | 'session.ended'
  | 'session.waiting'
  /** Module costs: a budget crossed its soft or hard limit, or a session's token rate ran away. */
  | 'cost.alert'
  /** Module audit-ops: a detection rule raised a security alert (new network, burst, refusals, two places). */
  | 'security.alert'
  | 'subagent.started'
  | 'subagent.finished'
  | 'workflow.started'
  | 'workflow.finished'
  | 'pipeline.item'
  | 'checkout.changed'
  | 'machine.online'
  | 'machine.offline'
  | 'action'
  | 'job';

export interface FeedRef {
  type: string;
  id: Id;
}

/** The activity stream. */
export interface FeedEvent {
  id: Id;
  at: Ts;
  machineId: Id;
  projectId: Id | null;
  kind: FeedEventKind;
  title: string;
  ref?: FeedRef;
}

export interface AuditEntry {
  id: Id;
  at: Ts;
  actor: string;
  action: ActionKind;
  target: string;
  machineId: Id;
  params: Record<string, unknown>;
  result: 'ok' | 'error';
  message?: string;
}

/** A git repo an agent found as the cwd of a recent Claude Code session; offered, never auto-added. */
export interface ProjectSuggestion {
  machineId: Id;
  path: string;
  name: string;
  remoteUrl: string | null;
  lastSessionAt: Ts | null;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type ActionKind =
  | 'session.start'
  | 'session.stop'
  | 'job.run'
  | 'job.cancel'
  | 'worktree.remove'
  | 'worktree.integrate'
  | 'worktree.landed'
  | 'pipeline.pause'
  | 'pipeline.resume'
  | 'pipeline.restart'
  | 'project.add'
  | 'project.remove';

export const ACTION_KINDS: readonly ActionKind[] = [
  'session.start',
  'session.stop',
  'job.run',
  'job.cancel',
  'worktree.remove',
  'worktree.integrate',
  'worktree.landed',
  'pipeline.pause',
  'pipeline.resume',
  'pipeline.restart',
  'project.add',
  'project.remove',
];

/** Either a known checkout or a raw directory on the machine. */
export type CheckoutTarget = { checkoutId: Id; cwd?: undefined } | { cwd: string; checkoutId?: undefined };

export type SessionStartMode = 'headless' | 'terminal';

export type SessionStartParams = CheckoutTarget & {
  /** Required for headless; optional for terminal (an empty Claude Code opens). */
  prompt?: string;
  mode: SessionStartMode;
  /**
   * The conversation to continue (claude --resume), same session id, in `cwd` (the session's
   * exact folder: Claude Code finds a transcript by it).
   * Terminal: ignored, with a note in the reply, while that session still has a live process
   * (two writers on one transcript would corrupt it), so a fresh Claude Code opens instead.
   * Headless (Approve and resume): the prompt goes on stdin; refused with code 'conflict'
   * while the session's process is alive, and refused with newWorktree.
   */
  resumeSessionId?: Id;
  /**
   * What the hub last saw of the session a headless resume continues (a wake, ADR 0049 decision
   * 11): its title and last words, for the note a new session starts with when the agent cannot
   * reopen it (an ACP agent), even after an agent restart. Ignored otherwise. Additive, 21 Oct 2026.
   */
  resumeContext?: { title?: string; lastText?: string };
  permissionMode?: string;
  model?: string;
  /**
   * A thought (reasoning) level the agent advertises (ACP session config category thought_level,
   * ADR 0049 decision 5). Only ACP agents take one; any other kind refuses it. Additive, 19 Oct 2026.
   */
  thoughtLevel?: string;
  newWorktree?: { branch: string };
  /**
   * The mission whose branch Orgus's integrator lands (ORGU-274): the agent passes it to the session
   * as ORGUS_MISSION_SESSION, and `orgus land` there defers to the integrator instead of submitting.
   * Older agents ignore it. Additive, 30 Oct 2026.
   */
  missionId?: Id;
  /** Which agent to start (default claude-code). */
  agentKind?: AgentKind;
  /**
   * The launch account to run on (ADR 0071). Absent: the project's default account, else none
   * (the machine's own sign-in and environment, exactly as before).
   */
  launchAccountId?: string;
};

/** The params each action kind takes. */
export interface ActionParamsMap {
  'session.start': SessionStartParams;
  'session.stop': { sessionId: Id };
  'job.run': CheckoutTarget & { command: string };
  'job.cancel': { jobId: Id };
  /**
   * Refuses when dirty or a live session's cwd is inside it. deleteBranch: then delete its branch
   * too, only when every commit of it is on main (`git branch -d`; ORGU-92, the land-cleanup rule).
   * collectShots (QA-75): data.shots carries the images under .orgus-dev/shots/ ({ name, data: base64 }).
   */
  'worktree.remove': { checkoutId: Id; deleteBranch?: boolean; collectShots?: boolean };
  /** Commit, merge main, land (ADR 0086 phase 1): ext/worktree-integrate.ts. */
  'worktree.integrate': WorktreeIntegrateParams;
  /** Is a branch tip on main, does the branch exist (QA-121), read-only: ext/worktree-integrate.ts. */
  'worktree.landed': WorktreeLandedParams;
  'pipeline.pause': { projectId: Id };
  'pipeline.resume': { projectId: Id };
  'pipeline.restart': { projectId: Id };
  'project.add': { machineId: Id; path: string };
  'project.remove': { projectId: Id; machineId: Id };
}

export type ActionParams<K extends ActionKind = ActionKind> = ActionParamsMap[K];

/** Body of `POST /api/actions`. */
export interface ActionRequest<K extends ActionKind = ActionKind> {
  machineId: Id;
  kind: K;
  params: ActionParamsMap[K];
}

/** Response of `POST /api/actions`. */
export interface ActionResponse {
  ok: boolean;
  message?: string;
  data?: unknown;
  auditId?: Id;
}

export function isActionKind(v: unknown): v is ActionKind {
  return typeof v === 'string' && (ACTION_KINDS as readonly string[]).includes(v);
}

// ---------------------------------------------------------------------------
// Agent <-> hub protocol (one WebSocket, JSON messages)
// ---------------------------------------------------------------------------

/** Collections an agent upserts. Projects are included so the hub knows names and remotes. */
export interface AgentUpserts {
  /** May be partial (ADR 0104): a node sends only load and free memory when nothing else changed. */
  machine?: Machine;
  projects?: Project[];
  checkouts?: Checkout[];
  sessions?: Session[];
  pipelines?: Pipeline[];
  jobs?: Job[];
  /**
   * The machine's whole suggestion list (ADR 0104): a node that reports transitions sends no
   * periodic snapshot, so a changed list comes here and replaces the last one. Absent: unchanged.
   */
  suggestions?: ProjectSuggestion[];
}

/** Ids an agent removes. Pipelines are removed by `pipelineKey`. */
export interface AgentRemoves {
  projects?: Id[];
  checkouts?: Id[];
  sessions?: Id[];
  pipelines?: string[];
  jobs?: Id[];
}

export interface HelloMessage {
  t: 'hello';
  machine: MachineInfo;
  secret?: string;
  joinToken?: string;
  version: string;
  protocol?: number;
}

export interface SnapshotMessage {
  t: 'snapshot';
  at: Ts;
  /**
   * ADR 0104: collections this snapshot leaves as the hub holds them (the node reconnected to
   * a hub that still has them, `welcome.fresh` absent); what changed meanwhile follows in a
   * patch. Only 'sessions' today. Absent: the snapshot replaces every collection, as before.
   */
  keep?: 'sessions'[];
  machine: Machine;
  projects: Project[];
  checkouts: Checkout[];
  sessions: Session[];
  pipelines: Pipeline[];
  jobs: Job[];
  suggestions: ProjectSuggestion[];
}

export interface PatchMessage {
  t: 'patch';
  at: Ts;
  upserts: AgentUpserts;
  removes: AgentRemoves;
}

/** The counters of one session since the last rollup, cumulative (the server replaces, never adds). */
export interface RollupRow {
  id: Id;
  lastActivity: Ts;
  turns: number;
  toolCalls: number;
  tokens: Session['tokens'];
  /** Cumulative usage per model (and per day when the agent has it), as Session.usage. */
  usage?: Session['usage'];
  /** Live usage (context fill, cache warmth, cost): Session.ext.usage, when the agent has it. */
  liveUsage?: unknown;
  /** True while the counts are a lower bound (the transcript is still being read). */
  partial?: boolean;
}

export interface RollupMessage {
  t: 'rollup';
  at: Ts;
  /** Per connection, from 1: the server ignores a rollup at or below the last one it applied. */
  seq: number;
  sessions: RollupRow[];
}

export interface EventMessage {
  t: 'event';
  event: FeedEvent;
}

export interface ActResultMessage {
  t: 'actResult';
  reqId: string;
  ok: boolean;
  message?: string;
  data?: unknown;
  /** How long the agent took over the action, from the act to this answer (ms); for the hub's action.timing line. */
  tookMs?: number;
}

/** A step a long action has reached, before its result. */
export type ActionStep = 'launching';

/**
 * A step of an action still running (session.start sends 'launching' once its new worktree
 * exists), so the hub can say what the machine is doing. Optional: a hub may ignore it.
 */
export interface ActStepMessage {
  t: 'actStep';
  reqId: string;
  step: ActionStep;
}

export interface LogsChunkMessage {
  t: 'logs.chunk';
  streamId: string;
  text: string;
  /** True when the stream has ended (job finished, item done). */
  end?: boolean;
}

export interface PingMessage {
  t: 'ping';
  /**
   * Optional since ADR 0104: a node sends the constant `{"t":"ping"}`, which the hosted server
   * answers without waking the node's object (WebSocket auto-response). A hub adds `at` to its own pings.
   */
  at?: Ts;
}

/** The ping a node sends, byte for byte (the hosted server's auto-response matches it exactly). */
export const NODE_PING_TEXT = '{"t":"ping"}';
export const NODE_PONG_TEXT = '{"t":"pong"}';

export interface PongMessage {
  t: 'pong';
  /** Absent in the hosted server's auto-response pong (NODE_PONG_TEXT). */
  at?: Ts;
}

export type AgentToHubMessage =
  | HelloMessage
  | SnapshotMessage
  | PatchMessage
  | RollupMessage
  | EventMessage
  | ActResultMessage
  | ActStepMessage
  | LogsChunkMessage
  | PingMessage
  | PongMessage
  | ExtMessage;

/**
 * A message owned by a feature module (approvals, costs, ...), either direction.
 * `ext` names the module; each module defines its own `type` and `data` in its own file
 * under packages/protocol/src/ext/. The core protocol never needs to change for a module.
 */
export interface ExtMessage {
  t: 'ext';
  ext: string;
  type: string;
  data: unknown;
  /** Set when the sender expects an answer with the same id. */
  id?: string;
}

export interface WelcomeMessage {
  t: 'welcome';
  machineId: Id;
  /** Present only on first enrolment: the agent stores it. */
  secret?: string;
  /**
   * ADR 0104: this hub takes `rollup` messages, so the node reports transitions and summaries
   * (ext/session-summary.ts) instead of whole sessions every second. Absent on an older hub.
   */
  transitions?: true;
  /**
   * ADR 0104: this hub holds nothing of this machine (it restarted, or the machine is new), so
   * the node sends everything; absent when the hub still holds the machine's state, and the
   * node may then send a snapshot that keeps its sessions and patch what changed.
   */
  fresh?: true;
}

export interface ActMessage<K extends ActionKind = ActionKind> {
  t: 'act';
  reqId: string;
  kind: K;
  params: ActionParamsMap[K];
}

export type LogRefType = 'job' | 'session' | 'pipelineItem';

export interface LogRef {
  type: LogRefType;
  id: Id;
}

export interface LogsSubscribeMessage {
  t: 'logs.subscribe';
  streamId: string;
  ref: LogRef;
}

export interface LogsUnsubscribeMessage {
  t: 'logs.unsubscribe';
  streamId: string;
}

/** The hub refuses the connection (bad token, revoked secret); the socket closes after it. */
export interface HubErrorMessage {
  t: 'error';
  code: 'unauthorized' | 'revoked' | 'bad-request' | 'version';
  message: string;
}

export type HubToAgentMessage =
  | WelcomeMessage
  | ActMessage
  | LogsSubscribeMessage
  | LogsUnsubscribeMessage
  | PingMessage
  | PongMessage
  | HubErrorMessage
  | ExtMessage;

// ---------------------------------------------------------------------------
// Hub <-> UI (HTTP + SSE)
// ---------------------------------------------------------------------------

/** `GET /api/state`, and the `state` SSE event. */
export interface AggregateState {
  at: Ts;
  machines: Machine[];
  projects: Project[];
  checkouts: Checkout[];
  sessions: Session[];
  pipelines: Pipeline[];
  jobs: Job[];
  /** Newest first. */
  feed: FeedEvent[];
  /** Newest first; may be a recent slice (full history via `GET /api/audit`). */
  audit: AuditEntry[];
  suggestions: ProjectSuggestion[];
  /** State owned by feature modules, keyed by module name (see packages/protocol/src/ext/). */
  ext?: Record<string, unknown>;
  /** True when the audit log holds entries older than `audit`. */
  auditMore?: boolean;
  /** The hub's recent samples of each machine's load and memory, oldest first. */
  machineHistory?: Record<Id, MachineMetricSample[]>;
}

/** Collections the hub upserts to the UI. */
export interface HubUpserts {
  machines?: Machine[];
  projects?: Project[];
  checkouts?: Checkout[];
  sessions?: Session[];
  pipelines?: Pipeline[];
  jobs?: Job[];
  suggestions?: ProjectSuggestion[];
}

/** Ids the hub removes. Pipelines by `pipelineKey`, suggestions by `suggestionKey`. */
export interface HubRemoves {
  machines?: Id[];
  projects?: Id[];
  checkouts?: Id[];
  sessions?: Id[];
  pipelines?: string[];
  jobs?: Id[];
  suggestions?: string[];
}

/** The `patch` SSE event. */
export interface StatePatch {
  at: Ts;
  upserts: HubUpserts;
  removes: HubRemoves;
}

/** The `log` SSE event. */
export interface LogChunk {
  streamId: string;
  ref: LogRef;
  text: string;
  end?: boolean;
}

/** Every SSE event on `GET /api/stream`: `event:` is the type, `data:` the JSON payload. */
export interface SseEventMap {
  state: AggregateState;
  patch: StatePatch;
  event: FeedEvent;
  audit: AuditEntry;
  log: LogChunk;
  /** Sent every SSE_PING_MS so the UI can tell a quiet stream from a dead one. */
  ping: { at: Ts };
  /** A feature module's live update: `ext` names the module. */
  ext: { ext: string; type: string; data: unknown };
  /** The last frame before the hub ends a stream whose credential no longer holds as it did (SD-2). */
  'auth-ended': AuthEndedEvent;
}

export type SseEventType = keyof SseEventMap;
export const SSE_EVENT_TYPES: readonly SseEventType[] = ['state', 'patch', 'event', 'audit', 'log', 'ping', 'ext', 'auth-ended'];

/** Every API error body: `{ error: { what, why, next } }`. */
export interface UiErrorShape {
  /** What failed, in a few words. */
  what: string;
  /** Why it failed. */
  why: string;
  /** What to do next. */
  next: string;
  code?: string;
}

export interface ApiErrorBody {
  error: UiErrorShape;
}

/** `POST /api/session`: exchange the owner token for a cookie. */
export interface SessionRequest {
  token: string;
}

/** `POST /api/join-tokens`. */
export interface JoinTokenResponse {
  token: string;
  expiresAt: Ts;
  /** The exact command to run on the new machine. */
  command: string;
  /** The same command for each shell. */
  commands?: { windows: string; posix: string };
  /** The hub address the command points at. */
  hubUrl?: string;
  /** Where to run the command. */
  runIn?: string;
  /** Set when no other machine can reach `hubUrl` (the hub listens on loopback only). */
  warning?: string;
}

/** `GET /api/feed?before=`. */
export interface FeedPage {
  events: FeedEvent[];
  more: boolean;
}

/** `GET /api/audit`. */
export interface AuditPage {
  entries: AuditEntry[];
  more: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DEFAULT_HUB_PORT = 4600;
export const DEFAULT_HUB_HOST = '127.0.0.1';
/** Vite dev server port used by `npm run dev`. */
export const DEFAULT_UI_DEV_PORT = 4610;

/** CSRF guard: every mutating request carries this header with value '1'. */
export const CSRF_HEADER = 'X-Orgus';
export const SESSION_COOKIE = 'orgus_session';

export const PING_INTERVAL_MS = 20_000;
/** The hub marks a machine offline after this long without a message. */
export const OFFLINE_AFTER_MS = 45_000;
export const SNAPSHOT_INTERVAL_MS = 60_000;
/** Agent patches are coalesced to at most one per this interval. */
export const PATCH_COALESCE_MS = 1_000;
export const RECONNECT_MIN_MS = 1_000;
export const RECONNECT_MAX_MS = 30_000;
export const ACTION_TIMEOUT_MS = 30_000;
export const SSE_PING_MS = 15_000;
/** The UI treats the stream as dead after this long without any SSE message. */
export const SSE_DEAD_AFTER_MS = 40_000;

export const GIT_INTERVAL_MS = 10_000;
export const PROCESS_INTERVAL_MS = 5_000;
export const SYSTEM_INTERVAL_MS = 5_000;

/** Session counts as working while there was activity this recently. */
export const SESSION_WORKING_WINDOW_MS = 60_000;
/** Only sessions with activity in this window are collected. */
export const SESSION_LOOKBACK_MS = 7 * 24 * 60 * 60_000;
export const SESSION_TITLE_MAX = 140;
export const SESSION_LAST_TEXT_MAX = 280;
/** What a session timeline keeps of one assistant message (line breaks kept, ADR 0056). */
export const SESSION_SAY_TEXT_MAX = 4000;

/** Stale thresholds: data older than this is shown as stale with its age. */
export const STALE_MS = {
  machine: OFFLINE_AFTER_MS,
  checkout: 6 * GIT_INTERVAL_MS,
  pipeline: 60_000,
  session: 2 * SNAPSHOT_INTERVAL_MS,
} as const;

/** A dirty worktree with no live session older than this needs the user. */
export const DIRTY_WORKTREE_NEEDS_YOU_MS = 24 * 60 * 60_000;

export const FEED_CAP = 10_000;
export const LOG_RING_BYTES = 2 * 1024 * 1024;
export const PIPELINE_RECENT_MAX = 20;
/** A join token works once, within 15 minutes (node-trust P0 item 17, CS-12). */
export const JOIN_TOKEN_TTL_MS = 15 * 60_000;

// ---------------------------------------------------------------------------
// Pure helpers (browser-safe; no hashing here: the agent computes ids)
// ---------------------------------------------------------------------------

/** Identity of a pipeline: one per project, machine and adapter. */
export function pipelineKey(p: Pick<Pipeline, 'projectId' | 'machineId' | 'adapter'>): string {
  return `${p.projectId}:${p.machineId}:${p.adapter}`;
}

export function suggestionKey(s: Pick<ProjectSuggestion, 'machineId' | 'path'>): string {
  return `${s.machineId}:${s.path}`;
}

/**
 * Normalize a git remote URL so every clone of one repo yields the same string:
 * `git@github.com:Org/Repo.git`, `https://github.com/org/repo` and
 * `ssh://git@github.com/org/repo.git` all become `github.com/org/repo`.
 * The agent hashes the result to get `Project.id`.
 */
export function normalizeRemoteUrl(url: string): string {
  let u = url.trim();
  const scp = /^[\w.-]+@([^:/]+):(.+)$/.exec(u);
  if (scp) u = `${scp[1]}/${scp[2]}`;
  else u = u.replace(/^[a-z+]+:\/\//i, '').replace(/^[^@/]+@/, '');
  u = u.replace(/:\d+(?=\/)/, '');
  return trimEndOf(u.replace(/\.git$/i, ''), '/').toLowerCase();
}

/** Last path segment of a remote URL or a path, without `.git`. */
export function repoNameOf(urlOrPath: string): string {
  const parts = trimEndOf(urlOrPath.replace(/\\/g, '/'), '/').split(/[/:]/);
  return (parts[parts.length - 1] ?? '').replace(/\.git$/i, '');
}

/** Online and heard from recently; an offline flag the hub has not set yet still counts as stale. */
export function machineLiveness(m: Pick<Machine, 'online' | 'lastSeen'>, now: Ts): 'online' | 'stale' | 'offline' {
  if (!m.online) return 'offline';
  return now - m.lastSeen > STALE_MS.machine ? 'stale' : 'online';
}

export function isStale(updatedAt: Ts, kind: keyof typeof STALE_MS, now: Ts): boolean {
  return now - updatedAt > STALE_MS[kind];
}

export function isRunActive(s: RunStatus): boolean {
  return s === 'running';
}

/** Truncate to `max` chars with an ellipsis. */
export function clip(text: string, max: number): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length <= max ? t : `${t.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

/** How far back a clip may move to land on a word or line boundary. */
const CLIP_BOUNDARY = 40;

/**
 * Like `clip`, but keeps the END: `…` and the last `max` chars, starting on a word. For text
 * whose point is at its end, such as a message that closes with a question (ADR 0056).
 */
export function clipTail(text: string, max: number): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const keep = Math.max(0, max - 1);
  let s = t.slice(t.length - keep);
  if (keep > 0 && t[t.length - keep - 1] !== ' ') {
    const i = s.indexOf(' ');
    if (i >= 0 && i <= CLIP_BOUNDARY) s = s.slice(i + 1);
  }
  return `…${s.trimStart()}`;
}

/**
 * Clip Markdown or other multi-line text to `max` chars, keeping its line breaks: the head
 * (`end` 'head', `…` after) or the tail (`end` 'tail', `…` before), never cut mid-word when a
 * boundary is near. CRLF becomes LF and runs of blank lines become one.
 */
export function clipText(text: string, max: number, end: 'head' | 'tail' = 'head'): string {
  const t = text.replace(/\r\n?/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  if (t.length <= max) return t;
  const keep = Math.max(0, max - 1);
  const ws = (c: string | undefined) => c === ' ' || c === '\n' || c === '\t';
  if (end === 'head') {
    let s = t.slice(0, keep);
    if (keep > 0 && !ws(t[keep])) {
      let i = s.length - 1;
      while (i >= 0 && s.length - i <= CLIP_BOUNDARY && !ws(s[i])) i--;
      if (i >= 0 && ws(s[i])) s = s.slice(0, i);
    }
    return `${s.trimEnd()}…`;
  }
  let s = t.slice(t.length - keep);
  // Start on a line when one begins near the cut (Markdown stays well formed), else on a word.
  const nl = s.indexOf('\n');
  if (nl >= 0 && nl <= CLIP_BOUNDARY * 10) return `…\n\n${s.slice(nl + 1).replace(/^\n+/, '')}`;
  if (keep > 0 && !ws(t[t.length - keep - 1])) {
    let i = 0;
    while (i < s.length && i <= CLIP_BOUNDARY && !ws(s[i])) i++;
    if (i < s.length && ws(s[i])) s = s.slice(i + 1);
  }
  return `…${s.trimStart()}`;
}

// ---------------------------------------------------------------------------
// Human labels (never render a raw enum value)
// ---------------------------------------------------------------------------

export const SESSION_STATUS_LABEL: Record<SessionStatus, string> = {
  working: 'Working',
  waiting: 'Waiting for you',
  idle: 'Idle',
  ended: 'Ended',
};

export const RUN_STATUS_LABEL: Record<RunStatus, string> = {
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  stopped: 'Stopped',
};

export const PIPELINE_STATE_LABEL: Record<PipelineState, string> = {
  running: 'Running',
  idle: 'Idle',
  paused: 'Paused',
  down: 'Down',
  error: 'Error',
};

export const PIPELINE_ITEM_STATE_LABEL: Record<PipelineItemState, string> = {
  queued: 'Queued',
  active: 'In progress',
  passed: 'Passed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const JOB_STATE_LABEL: Record<JobState, string> = {
  running: 'Running',
  passed: 'Passed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const ACTION_LABEL: Record<ActionKind, string> = {
  'session.start': 'Start session',
  'session.stop': 'Stop session',
  'job.run': 'Run command',
  'job.cancel': 'Cancel command',
  'worktree.remove': 'Remove worktree',
  'worktree.integrate': 'Merge main and land',
  'worktree.landed': 'Check a branch landed',
  'pipeline.pause': 'Pause pipeline',
  'pipeline.resume': 'Resume pipeline',
  'pipeline.restart': 'Restart pipeline',
  'project.add': 'Add project',
  'project.remove': 'Remove project',
};

export const FEED_KIND_LABEL: Record<FeedEventKind, string> = {
  'session.started': 'Session started',
  'session.ended': 'Session ended',
  'session.waiting': 'Waiting for you',
  'cost.alert': 'Spend',
  'security.alert': 'Security alert',
  'subagent.started': 'Subagent started',
  'subagent.finished': 'Subagent finished',
  'workflow.started': 'Workflow started',
  'workflow.finished': 'Workflow finished',
  'pipeline.item': 'Pipeline',
  'checkout.changed': 'Checkout changed',
  'machine.online': 'Machine online',
  'machine.offline': 'Machine offline',
  action: 'Action',
  job: 'Command',
};

export const OS_LABEL: Record<MachineOs, string> = {
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
};

// Feature modules: one file each under ./ext/, re-exported here (one line per module).
export * from './ext/index.ts';
export * from './signin-hint.ts';
